import Utils from "../../../../../../core/services/utils/utils";

export const PLANNING_DRAFT_HISTORY_CAP = 50;

export const FORECASTED_QTY_COLUMN = 'Forecasted Qty';

export const DISTRIBUTED_QTY_COLUMN_PREFIX = 'Distributed Qty - ';

export type PlanningDraftChangeKind =
    | 'import'
    | 'updateQty'
    | 'updateForecastedQty'
    | 'updateNote'
    | 'updatePart'
    | 'addRow'
    | 'removeRow'
    | 'reorder'
    | 'distributeQty';

export type PlanningDraftChangeTone = 'primary' | 'secondary' | 'tertiary' | 'disabled';

export interface PlanningDraftChangeSegment {
    text: string;
    tone?: PlanningDraftChangeTone;
}

export interface PlanningDraftChange {
    id: string;
    kind: PlanningDraftChangeKind;
    label: string;
    at: number;
    segments?: PlanningDraftChangeSegment[];
    orderIndex?: number;
    partNumber?: string;
}

export interface VendorDistribution {
    vendorCode: string;
    qty: number;
}

export interface PlanningDraftRow {
    _id: string;
    _changed: boolean;
    orderIndex?: number;
    partNumber?: string;
    qty?: number;
    forecastedQty?: number;
    note?: string;
    vendorDistributions?: VendorDistribution[];
}

export interface PlanningDraftHistoryEntry<T> {
    change: PlanningDraftChange;
    snapshot: T[];
}

/**
 * File-column title for one vendor's distributed quantity.
 * @param vendorCode
 */
export const distributedQtyColumnTitle = (vendorCode: string) => `${DISTRIBUTED_QTY_COLUMN_PREFIX}${vendorCode}`;

/**
 * Reads a vendor code from a distributed-qty column title.
 * @param title
 */
export const vendorCodeFromDistributedQtyColumn = (title: string): string | undefined => {
    if (!title.toLowerCase().startsWith(DISTRIBUTED_QTY_COLUMN_PREFIX.toLowerCase()))
        return undefined;
    const vendorCode = title.slice(DISTRIBUTED_QTY_COLUMN_PREFIX.length).trim();
    return vendorCode || undefined;
};

/**
 * Signature of the fields Save and Calculate persist.
 * Rows without a part number are scratch rows and are ignored.
 * @param items
 */
export const serverSignature = (items: PlanningDraftRow[]): string => JSON.stringify(
    items
        .filter(item => !!item.partNumber?.trim())
        .map(item => ({
            orderIndex: item.orderIndex ?? 0,
            partNumber: item.partNumber ?? '',
            qty: item.qty ?? 0,
            forecastedQty: item.forecastedQty ?? 0,
            note: (item.note ?? '').trim(),
        })),
);

const normalizedDistributions = (distributions?: VendorDistribution[]) => (
    [...(distributions ?? [])]
        .filter(entry => !!entry.vendorCode && (entry.qty ?? 0) > 0)
        .map(entry => ({vendorCode: entry.vendorCode, qty: entry.qty ?? 0}))
        .sort((a, b) => a.vendorCode.localeCompare(b.vendorCode))
);

/**
 * Signature of session-only vendor allocations. Empty allocations are omitted.
 * @param items
 */
export const allocationSignature = (items: PlanningDraftRow[]): string => JSON.stringify(
    items
        .flatMap(item => {
            const distributions = normalizedDistributions(item.vendorDistributions);
            if (!item._id || !distributions.length)
                return [];
            return [[item._id, distributions] as const];
        })
        .sort((a, b) => a[0].localeCompare(b[0])),
);

/**
 * Copies vendor allocations from the working rows onto a server snapshot.
 * @param baseline
 * @param current
 */
export const reattachAllocations = <T extends PlanningDraftRow>(baseline: T[], current: T[]): T[] => (
    baseline.map(row => {
        const match = current.find(item => item._id === row._id)
            ?? current.find(item => item.orderIndex === row.orderIndex && item.partNumber === row.partNumber);
        return {
            ...row,
            vendorDistributions: Utils.deepCopy(match?.vendorDistributions ?? []),
        };
    })
);

/**
 * Overlays a save or calculate response onto the working rows.
 * Missing response rows keep the local plan. Blank rows stay at the end.
 * Vendor allocations are copied back only for vendors on the saved row.
 * @param current
 * @param incoming
 * @param createId
 */
export const mergeServerRows = <T extends PlanningDraftRow>(
    current: T[],
    incoming: Array<Partial<T>> | null | undefined,
    createId: () => string,
): T[] => {
    if (!incoming) {
        return current.map(row => ({...row, _changed: false}));
    }
    const merged = incoming.map(record => {
        const match = current.find(row => !!row.partNumber && row.orderIndex === record.orderIndex && row.partNumber === record.partNumber)
            ?? current.find(row => row.orderIndex === record.orderIndex && !!row.partNumber);
        const savedRow = record as Partial<T> & {
            vendorsOfferings?: Array<{ vendorCode?: string }>;
            onOrderItems?: Array<{ vendorCode?: string }>;
        };
        const knownVendors = knownVendorCodes(savedRow);
        return {
            ...record,
            forecastedQty: record.forecastedQty ?? match?.forecastedQty ?? 0,
            vendorDistributions: Utils.deepCopy(
                (match?.vendorDistributions ?? []).filter(entry => knownVendors.has(entry.vendorCode)),
            ),
            _id: match?._id ?? createId(),
            _changed: false,
        } as T;
    }).sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    const blanks = current.filter(row => !row.partNumber);
    return [...merged, ...blanks];
};

const knownVendorCodes = (record: {
    vendorsOfferings?: Array<{ vendorCode?: string }>;
    onOrderItems?: Array<{ vendorCode?: string }>;
}): Set<string> => new Set(
    [
        ...(record.vendorsOfferings ?? []).map(item => item.vendorCode),
        ...(record.onOrderItems ?? []).map(item => item.vendorCode),
    ].filter((code): code is string => !!code),
);

export interface DroppedVendorDistribution {
    partNumber: string;
    vendors: Array<{ vendorCode: string; qty: number }>;
}

/**
 * Vendors allocated on the working rows that the saved plan does not connect to the part.
 * @param current
 * @param incoming
 */
export const droppedVendorDistributions = <T extends PlanningDraftRow>(
    current: T[],
    incoming: Array<Partial<T>> | null | undefined,
): DroppedVendorDistribution[] => {
    if (!incoming)
        return [];
    const grouped = new Map<string, Map<string, number>>();
    for (const record of incoming) {
        const match = current.find(row => !!row.partNumber && row.orderIndex === record.orderIndex && row.partNumber === record.partNumber)
            ?? current.find(row => row.orderIndex === record.orderIndex && !!row.partNumber);
        if (!match?.partNumber)
            continue;
        const known = knownVendorCodes(record as {
            vendorsOfferings?: Array<{ vendorCode?: string }>;
            onOrderItems?: Array<{ vendorCode?: string }>;
        });
        const vendors = grouped.get(match.partNumber) ?? new Map<string, number>();
        for (const entry of match.vendorDistributions ?? []) {
            if (!entry.vendorCode || known.has(entry.vendorCode))
                continue;
            vendors.set(entry.vendorCode, (vendors.get(entry.vendorCode) ?? 0) + (entry.qty ?? 0));
        }
        if (vendors.size)
            grouped.set(match.partNumber, vendors);
    }
    return [...grouped.entries()].map(([partNumber, vendors]) => ({
        partNumber,
        vendors: [...vendors.entries()].map(([vendorCode, qty]) => ({vendorCode, qty})),
    }));
};

export type PurchasePlanSaveKind = 'updateDetails' | 'forecast' | 'added' | 'removed' | 'part' | 'reordered';

export interface PurchasePlanSaveOperation {
    id: string;
    kind: PurchasePlanSaveKind;
    partNumber: string;
    orderIndex: number;
    fromPart?: string;
    qty?: number;
    note?: string;
}

const rowsWithPart = (items: PlanningDraftRow[]) => items.filter(row => !!row.partNumber?.trim());

const rowKey = (orderIndex: number, partNumber: string) => `${orderIndex}\u0000${partNumber}`;

/**
 * Splits a dirty plan into the per-row update the server already supports,
 * and the edits that still have no API.
 * A row is the same row when its order index and part number are unchanged.
 * @param baseline
 * @param current
 */
export const buildPurchasePlanSaveOperations = (
    baseline: PlanningDraftRow[],
    current: PlanningDraftRow[],
): PurchasePlanSaveOperation[] => {
    const saved = rowsWithPart(baseline);
    const next = rowsWithPart(current);
    const operations: PurchasePlanSaveOperation[] = [];
    const consumedSaved = new Set<number>();
    const consumedNext = new Set<number>();

    saved.forEach((previous, savedIndex) => {
        const nextIndex = next.findIndex((row, index) => (
            !consumedNext.has(index)
            && (row.orderIndex ?? 0) === (previous.orderIndex ?? 0)
            && row.partNumber === previous.partNumber
        ));
        if (nextIndex === -1)
            return;
        consumedSaved.add(savedIndex);
        consumedNext.add(nextIndex);
        const row = next[nextIndex];
        const identity = rowKey(row.orderIndex ?? 0, row.partNumber ?? '');
        if ((previous.qty ?? 0) !== (row.qty ?? 0) || (previous.note ?? '').trim() !== (row.note ?? '').trim()) {
            operations.push({
                id: `updateDetails:${identity}`,
                kind: 'updateDetails',
                partNumber: row.partNumber ?? '',
                orderIndex: row.orderIndex ?? 0,
                qty: row.qty ?? 0,
                note: row.note ?? '',
            });
        }
        if ((previous.forecastedQty ?? 0) !== (row.forecastedQty ?? 0)) {
            operations.push({
                id: `forecast:${identity}`,
                kind: 'forecast',
                partNumber: row.partNumber ?? '',
                orderIndex: row.orderIndex ?? 0,
            });
        }
    });

    saved.forEach((previous, savedIndex) => {
        if (consumedSaved.has(savedIndex))
            return;
        const nextIndex = next.findIndex((row, index) => (
            !consumedNext.has(index) && (row.orderIndex ?? 0) === (previous.orderIndex ?? 0)
        ));
        if (nextIndex === -1)
            return;
        const row = next[nextIndex];
        const oldPartMoved = next.some((candidate, index) => (
            !consumedNext.has(index) && index !== nextIndex && candidate.partNumber === previous.partNumber
        ));
        const newPartMoved = saved.some((candidate, index) => (
            !consumedSaved.has(index) && index !== savedIndex && candidate.partNumber === row.partNumber
        ));
        if (oldPartMoved || newPartMoved)
            return;
        consumedSaved.add(savedIndex);
        consumedNext.add(nextIndex);
        operations.push({
            id: `part:${row.orderIndex ?? 0}`,
            kind: 'part',
            partNumber: row.partNumber ?? '',
            fromPart: previous.partNumber,
            orderIndex: row.orderIndex ?? 0,
        });
    });

    saved.forEach((previous, savedIndex) => {
        if (consumedSaved.has(savedIndex))
            return;
        const nextIndex = next.findIndex((row, index) => !consumedNext.has(index) && row.partNumber === previous.partNumber);
        if (nextIndex === -1)
            return;
        consumedSaved.add(savedIndex);
        consumedNext.add(nextIndex);
        const row = next[nextIndex];
        operations.push({
            id: `reordered:${rowKey(row.orderIndex ?? 0, row.partNumber ?? '')}`,
            kind: 'reordered',
            partNumber: row.partNumber ?? '',
            orderIndex: row.orderIndex ?? 0,
        });
    });

    next.forEach((row, index) => {
        if (consumedNext.has(index))
            return;
        operations.push({
            id: `added:${rowKey(row.orderIndex ?? 0, row.partNumber ?? '')}:${index}`,
            kind: 'added',
            partNumber: row.partNumber ?? '',
            orderIndex: row.orderIndex ?? 0,
        });
    });

    saved.forEach((row, index) => {
        if (consumedSaved.has(index))
            return;
        operations.push({
            id: `removed:${rowKey(row.orderIndex ?? 0, row.partNumber ?? '')}:${index}`,
            kind: 'removed',
            partNumber: row.partNumber ?? '',
            orderIndex: row.orderIndex ?? 0,
        });
    });

    return operations;
};
